import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Heart, Shield, Lock, Sparkles } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_908682e2-cfa4-4698-8b7e-10e737f43eaf/artifacts/y2fvty9k_20250404_015718.jpg";
const HERO = "https://customer-assets.emergentagent.com/job_908682e2-cfa4-4698-8b7e-10e737f43eaf/artifacts/2tguekwo_20250404_015606.png";

export default function Landing() {
  return (
    <div className="min-h-screen" style={{ background: "var(--lavender)" }}>
      <nav className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between" data-testid="landing-nav">
        <Link to="/" className="flex items-center gap-3">
          <img src={LOGO} alt="Purple Partnerships" className="h-12 w-12 rounded-xl object-cover" />
          <span className="font-display text-xl font-bold text-white">Purple Partnerships</span>
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/login"><Button variant="ghost" className="text-white hover:bg-white/10 rounded-full" data-testid="landing-login-btn">Log in</Button></Link>
          <Link to="/register"><Button className="bg-[#FF1493] hover:bg-[#d1107a] text-white font-bold rounded-full px-6 pulse-glow" data-testid="landing-cta-btn">Get Started</Button></Link>
        </div>
      </nav>

      <section className="max-w-7xl mx-auto px-6 pt-8 pb-20 grid lg:grid-cols-2 gap-12 items-center fade-up">
        <div>
          <p className="neon-cyan-text font-display text-sm tracking-widest mb-4">21+ · LGBTQIA+ · SCREENED · SAFE</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold text-white leading-tight">
            Lavender marriages,<br />
            <span className="neon-pink-text">done right.</span>
          </h1>
          <p className="text-white/90 text-lg sm:text-xl mt-6 max-w-xl">
            Real partnerships for real life. Built for queer folks, non-binary folks, and the open-minded who want a marriage of <em>chosen family</em> — not a romantic rollercoaster. No catfish. No predators. No cap.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <Link to="/register"><Button className="bg-[#FF1493] hover:bg-[#d1107a] text-white font-bold rounded-full px-8 py-6 text-base" data-testid="hero-cta-btn">Start the screening</Button></Link>
            <Link to="/legal/disclaimer"><Button variant="outline" className="border-white text-white hover:bg-white hover:text-black rounded-full px-8 py-6 text-base bg-transparent" data-testid="hero-legal-btn">Read the safety policies</Button></Link>
          </div>
          <div className="flex flex-wrap gap-6 mt-10 text-white/90 text-sm">
            <span className="flex items-center gap-2"><Shield size={16} className="text-[#00FFFF]" /> ID verified members</span>
            <span className="flex items-center gap-2"><Lock size={16} className="text-[#00FFFF]" /> Encrypted messaging</span>
            <span className="flex items-center gap-2"><Heart size={16} className="text-[#FF1493]" /> Real intent only</span>
          </div>
        </div>
        <div className="relative">
          <img src={HERO} alt="Purple Partnerships couple" className="rounded-3xl w-full object-cover shadow-2xl" data-testid="hero-image" />
          <div className="absolute -bottom-6 -left-6 glass rounded-2xl p-5 max-w-xs">
            <p className="font-display text-white text-sm">"Found my person. Real partnership, real future."</p>
            <p className="text-white/60 text-xs mt-1">— Asha & Marcus, married 2025</p>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-16">
        <h2 className="font-display text-3xl sm:text-4xl font-bold text-white text-center mb-12">How we keep it safe</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { t: "Strict 21+ Screening", d: "Date-of-birth verification, identity questionnaires, and intent screening before you can browse a single profile.", i: <Shield className="text-[#00FFFF]" /> },
            { t: "AI Photo Moderation", d: "Every photo runs through AI to block nudity, group shots, and blurry uploads. 8MP minimum. No catfish.", i: <Sparkles className="text-[#FF1493]" /> },
            { t: "Encrypted DMs", d: "Platinum & Diamond members chat through end-to-end encrypted, in-app messaging only. Off-platform contact is never required.", i: <Lock className="text-[#00FFFF]" /> },
          ].map((c) => (
            <div key={c.t} className="glass rounded-2xl p-8 fade-up" data-testid={`safety-card-${c.t.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mb-4">{c.i}</div>
              <h3 className="font-display text-xl font-bold text-white mb-2">{c.t}</h3>
              <p className="text-white/80">{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="max-w-7xl mx-auto px-6 py-10 border-t border-white/15 text-white/80 text-sm flex flex-wrap gap-4 justify-between">
        <span>© 2026 Purple Partnerships. 21+ only.</span>
        <div className="flex gap-4">
          <Link to="/legal/terms" className="hover:text-white">Terms</Link>
          <Link to="/legal/privacy" className="hover:text-white">Privacy</Link>
          <Link to="/legal/disclaimer" className="hover:text-white">Disclaimer</Link>
          <Link to="/legal/guidelines" className="hover:text-white">Community Guidelines</Link>
        </div>
      </footer>
    </div>
  );
}
